/**
 * Automations API Layer.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { AutomationRule, AutomationRuleCreate } from "@/types/automation";

export function useAutomationRules() {
  return useQuery<AutomationRule[]>({
    queryKey: ["automations"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/automations")).data as AutomationRule[],
  });
}

export function useCreateAutomationRule() {
  const qc = useQueryClient();
  return useMutation<AutomationRule, Error, AutomationRuleCreate>({
    mutationFn: async (data) =>
      (await apiClient.post("/api/v1/automations", data))
        .data as AutomationRule,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["automations"] }),
  });
}

export function useUpdateAutomationRule(ruleId: string) {
  const qc = useQueryClient();
  return useMutation<AutomationRule, Error, Partial<AutomationRule>>({
    mutationFn: async (data) =>
      (await apiClient.put(`/api/v1/automations/${ruleId}`, data))
        .data as AutomationRule,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["automations"] }),
  });
}

export function useDeleteAutomationRule() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (ruleId) => {
      await apiClient.delete(`/api/v1/automations/${ruleId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["automations"] }),
  });
}
